<template>
  <div class="settlement-page-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Header -->
    <v-card flat class="settlement-header-card pa-4 mb-4 rounded-lg">
      <v-layout wrap align-center justify-space-between>
        <v-flex xs12 sm6 class="d-flex align-center">
          <v-btn icon small class="mr-2 header-back-btn" @click="$router.push('/SalesList')">
            <v-icon small color="#475569">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="d-flex align-center">
              <span class="header-title">BILLING & SETTLEMENT</span>
              <span v-if="estno" class="estimate-pill ml-3">#{{ (saledata && saledata.billPrefix ? saledata.billPrefix : '') + estno }}</span>
            </div>
            <div class="header-subtitle">Review estimate items, apply exchanges & finalize payment</div>
          </div>
        </v-flex>

        <v-flex xs12 sm6 class="d-flex justify-end align-center mt-2 mt-sm-0">
          <div class="meta-badge mr-3" v-if="date">
            <v-icon small color="#64748b" class="mr-1">mdi-calendar-range</v-icon>
            <span>{{ date }}</span>
          </div>
          <div class="meta-badge" v-if="salesmenCode">
            <v-icon small color="#64748b" class="mr-1">mdi-account-tie</v-icon>
            <span>Staff: {{ salesmenCode }}</span>
          </div>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Customer Summary Strip -->
    <v-card flat class="customer-card pa-4 mb-4 rounded-lg">
      <div class="d-flex align-center mb-3">
        <v-icon small color="#475569" class="mr-2">mdi-account-outline</v-icon>
        <span class="card-section-title">Customer Information</span>
      </div>
      <v-row dense>
        <v-col cols="12" sm="4">
          <div class="info-group">
            <span class="info-label">Customer Name</span>
            <span class="info-val font-weight-bold text-slate-900">{{ customerName || '-' }}</span>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="info-group">
            <span class="info-label">Mobile Number</span>
            <span class="info-val">{{ contact || '-' }}</span>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="info-group">
            <span class="info-label">Address & City</span>
            <span class="info-val">{{ [address, city].filter(Boolean).join(', ') || '-' }}</span>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Bill Items Card & Table (Only rendered if items exist) -->
    <v-card flat class="pro-content-card mb-4 rounded-lg overflow-hidden" v-if="List && List.length > 0">
      <div class="card-bar px-4 py-3 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon small color="#475569" class="mr-2">mdi-ring</v-icon>
          <span class="card-section-title">Purchased Items</span>
          <span class="badge-count ml-2">{{ List.length }} items</span>
        </div>
      </div>

      <div class="table-responsive">
        <v-simple-table class="pro-table m-stack-table m-stack-grid">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 50px;">#</th>
                <th class="text-left">Item Description</th>
                <th class="text-left" style="width: 100px;">HSN</th>
                <th class="text-center" style="width: 70px;">Qty</th>
                <th class="text-right">St. Wt (g)</th>
                <th class="text-right">Net Wt (g)</th>
                <th class="text-right">St. Price (₹)</th>
                <th class="text-right">Making Chg (₹)</th>
                <th class="text-right">VA (%)</th>
                <th class="text-right">Discount (₹)</th>
                <th class="text-right">Total (₹)</th>
                <th class="text-right" v-if="taxType == 'true'">GST Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in List" :key="i">
                <td data-label="#" class="text-muted">{{ i + 1 }}</td>
                <td data-label="Item Description" class="font-weight-medium">
                  <span v-if="value.designId">{{ value.designId.name }} - {{ value.itemId.name }}</span>
                  <span v-else>-</span>
                </td>
                <td data-label="HSN">
                  <span v-if="value.designId && value.designId.HSN">{{ value.designId.HSN.hsnCode }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td data-label="Qty" class="text-center">{{ value.quantity || '-' }}</td>
                <td data-label="St. Wt (g)" class="text-right">{{ value.stonWeight || '0.00' }}</td>
                <td data-label="Net Wt (g)" class="text-right font-weight-medium">{{ value.netWeight || '0.00' }}</td>
                <td data-label="St. Price (₹)" class="text-right">{{ value.stonWeight ? Number(value.stonWeight).toFixed(2) : '0.00' }}</td>
                <td data-label="Making Chg (₹)" class="text-right">
                  {{ value.adjustedMakingCharge ? Number(value.adjustedMakingCharge).toFixed(2) : (value.makingCharge ? Number(value.makingCharge).toFixed(2) : '0.00') }}
                </td>
                <td data-label="VA (%)" class="text-right">
                  {{ value.valueAddition ? Number(value.valueAddition).toFixed(2) : '0.00' }}
                </td>
                <td data-label="Discount (₹)" class="text-right">{{ value.discountAmount ? Number(value.discountAmount).toFixed(2) : '0.00' }}</td>
                <td data-label="Total (₹)" class="text-right">{{ value.amountWithoutGst ? Number(value.amountWithoutGst).toFixed(2) : '0.00' }}</td>
                <td data-label="GST Total (₹)" class="text-right font-weight-bold text-slate-900" v-if="taxType == 'true'">
                  ₹{{ value.amountWithGst ? Number(value.amountWithGst).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00' }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-card>

    <!-- Old Gold Exchange Collapsible Card -->
    <v-card flat class="pro-content-card mb-4 rounded-lg overflow-hidden" v-if="List2 && List2.length > 0">
      <div class="card-bar px-4 py-3 d-flex align-center justify-space-between cursor-pointer" @click="show1 = !show1">
        <div class="d-flex align-center">
          <v-icon small color="#64748b" class="mr-2">mdi-swap-horizontal</v-icon>
          <span class="card-section-title">Old Metal Exchange Items</span>
          <span class="badge-count ml-2">{{ List2.length }} items</span>
        </div>
        <v-btn icon small>
          <v-icon small>{{ show1 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="show1">
          <v-simple-table class="pro-table m-stack-table m-stack-grid">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="width: 50px;">#</th>
                  <th class="text-left">Item Name</th>
                  <th class="text-center">Metal</th>
                  <th class="text-right">Rate (₹)</th>
                  <th class="text-center">Qty</th>
                  <th class="text-right">Gross Wt (g)</th>
                  <th class="text-right">Stone Wt (g)</th>
                  <th class="text-right">Net Wt (g)</th>
                  <th class="text-right">Melt (%)</th>
                  <th class="text-right">{{ isStandalone ? 'Payable (₹)' : 'Deduction (₹)' }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(value2, i) in List2" :key="i">
                  <td data-label="#" class="text-muted">{{ i + 1 }}</td>
                  <td data-label="Item Name" class="font-weight-medium">{{ value2.itemName || '-' }}</td>
                  <td data-label="Metal" class="text-center">{{ value2.metalType || 'Gold' }}</td>
                  <td data-label="Rate (₹)" class="text-right">{{ value2.goldRate || '0.00' }}</td>
                  <td data-label="Qty" class="text-center">{{ value2.quantity || '-' }}</td>
                  <td data-label="Gross Wt (g)" class="text-right">{{ value2.grossWeight || '0.00' }}</td>
                  <td data-label="Stone Wt (g)" class="text-right">{{ value2.stoneWeight || '0.00' }}</td>
                  <td data-label="Net Wt (g)" class="text-right font-weight-medium">{{ value2.netWeight || '0.00' }}</td>
                  <td data-label="Melt (%)" class="text-right">{{ value2.melt || '0' }}%</td>
                  <td data-label="Payable (₹)" class="text-right font-weight-semibold text-emerald-700">
                    {{ isStandalone ? '' : '- ' }}₹{{ value2.itemAmount ? Number(value2.itemAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00' }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Return Gold Credit Collapsible Card -->
    <v-card flat class="pro-content-card mb-4 rounded-lg overflow-hidden" v-if="List3 && List3.length > 0">
      <div class="card-bar px-4 py-3 d-flex align-center justify-space-between cursor-pointer" @click="show = !show">
        <div class="d-flex align-center">
          <v-icon small color="#64748b" class="mr-2">mdi-keyboard-return</v-icon>
          <span class="card-section-title">Return Gold / Credit Note</span>
          <span class="badge-count ml-2">{{ List3.length }} items</span>
        </div>
        <v-btn icon small>
          <v-icon small>{{ show ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="show">
          <v-simple-table class="pro-table m-stack-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="width: 50px;">#</th>
                  <th class="text-left">Item Description</th>
                  <th class="text-left">HSN</th>
                  <th class="text-center">Qty</th>
                  <th class="text-right">Gross Wt (g)</th>
                  <th class="text-right">Stone Wt (g)</th>
                  <th class="text-right">Net Wt (g)</th>
                  <th class="text-right">{{ isStandalone ? 'Payable (₹)' : 'Credit (₹)' }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(value3, i) in List3" :key="i">
                  <td data-label="#" class="text-muted">{{ i + 1 }}</td>
                  <td data-label="Item Description" class="font-weight-medium">
                    <span v-if="value3.designId">{{ value3.designId.name }} - {{ value3.itemId.name }}</span>
                    <span v-else>-</span>
                  </td>
                  <td data-label="HSN">{{ value3.designId && value3.designId.HSN ? value3.designId.HSN.hsnCode : '-' }}</td>
                  <td data-label="Qty" class="text-center">{{ value3.quantity || 0 }}</td>
                  <td data-label="Gross Wt (g)" class="text-right">{{ value3.grossWeight || '0.00' }}</td>
                  <td data-label="Stone Wt (g)" class="text-right">{{ value3.stonWeight || '0.00' }}</td>
                  <td data-label="Net Wt (g)" class="text-right font-weight-medium">{{ value3.netWeight || '0.00' }}</td>
                  <td data-label="Payable (₹)" class="text-right font-weight-semibold text-emerald-700">
                    {{ isStandalone ? '' : '- ' }}₹{{ value3.itemTotalAmount ? Number(value3.itemTotalAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00' }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Payment Breakdown & Tender Section -->
    <v-card flat class="pro-content-card mb-4 rounded-lg pa-4">
      <v-row>
        <!-- Left Panel: Financial Summary Breakdown -->
        <v-col cols="12" md="6" class="pr-md-6 border-right-md d-flex flex-column justify-space-between">
          <div>
            <div class="d-flex align-center mb-3">
              <v-icon small color="#475569" class="mr-2">mdi-calculator</v-icon>
              <span class="card-section-title">Settlement Breakdown</span>
            </div>

            <div class="breakdown-list">
              <div class="breakdown-row" v-if="ItemTotal && !isStandalone">
                <span class="breakdown-label">Sales Gross Amount:</span>
                <span class="breakdown-val">
                  ₹ {{ Number(ItemTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  <small class="text-muted ml-1" v-if="saledata && saledata.totalNetWeight">({{ saledata.totalNetWeight.toFixed(2) }} g)</small>
                </span>
              </div>

              <div class="breakdown-row deduction-row" v-if="OldGoldTotal && Number(OldGoldTotal) > 0">
                <span class="breakdown-label">{{ isStandalone ? 'Old Gold/Silver Payable:' : 'Old Gold Exchange:' }}</span>
                <span class="breakdown-val">
                  {{ isStandalone ? '' : '- ' }}₹ {{ Number(OldGoldTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  <small class="ml-1" v-if="oldGoldData && oldGoldData.totalNetWeight">({{ oldGoldData.totalNetWeight.toFixed(2) }} g)</small>
                </span>
              </div>

              <div class="breakdown-row deduction-row" v-if="returnItemTotal && Number(returnItemTotal) > 0">
                <span class="breakdown-label">{{ isStandalone ? 'Return Payable:' : 'Return Gold Credit:' }}</span>
                <span class="breakdown-val">
                  {{ isStandalone ? '' : '- ' }}₹ {{ Number(returnItemTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                  <small class="ml-1" v-if="returnItemNetWt">({{ returnItemNetWt }} g)</small>
                </span>
              </div>

              <div class="scheme-box pa-3 my-2 rounded-lg" v-if="redeemableSchemes.length > 0 && !isStandalone && paymentStatus == 'Pending'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="advance-title">Gold Scheme Balance:</span>
                  <span class="advance-avail-val">₹ {{ Number(schemeAvailableTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
                </div>
                <div
                  v-for="scheme in redeemableSchemes"
                  :key="scheme._id"
                  class="d-flex align-center justify-space-between"
                >
                  <v-checkbox
                    :input-value="selectedSchemeIds.includes(scheme._id)"
                    @change="toggleScheme(scheme._id)"
                    color="#9a6b27"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                    :label="(scheme.schemeNumber || 'Scheme') + ' · ' + ((scheme.goldSchemeId && scheme.goldSchemeId.goldSchemeName) || 'Gold Scheme')"
                  ></v-checkbox>
                  <span class="breakdown-val">₹ {{ Number(scheme.availableAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
                </div>
              </div>

              <div
                class="breakdown-row deduction-row"
                v-for="alloc in schemeAllocations"
                :key="alloc.id"
              >
                <span class="breakdown-label">Gold Scheme {{ alloc.schemeNumber }}:</span>
                <span class="breakdown-val">
                  - ₹ {{ Number(alloc.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                </span>
              </div>

              <div class="breakdown-row" v-if="schemeAppliedAmount && Number(schemeAppliedAmount) > 0">
                <span class="breakdown-label">Scheme Balance Remaining:</span>
                <span class="breakdown-val">₹ {{ Number(newSchemeBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
              </div>

              <div class="breakdown-row" v-if="paymentStatus == 'PartiallyPaid' && actualBillTotal">
                <span class="breakdown-label">Original Bill Total:</span>
                <span class="breakdown-val">₹ {{ Number(actualBillTotal).toFixed(2) }}</span>
              </div>

              <div class="breakdown-row" v-if="billPaidAmount1 && Number(billPaidAmount1) > 0">
                <span class="breakdown-label">Previously Paid:</span>
                <span class="breakdown-val">₹ {{ Number(billPaidAmount1).toFixed(2) }}</span>
              </div>

              <!-- Customer Advance Available & Input -->
              <div class="customer-advance-box pa-3 my-2 rounded-lg" v-if="customerAdvanceAvailable > 0 && !billPaidAmount1 && !isStandalone">
                <div class="d-flex justify-space-between align-center mb-2">
                  <span class="advance-title">Customer Advance Balance:</span>
                  <span class="advance-avail-val">₹ {{ customerAdvanceAvailable.toFixed(2) }}</span>
                </div>
                <div class="d-flex align-center">
                  <v-text-field
                    outlined
                    placeholder="Enter advance amount"
                    v-model="advanceInput"
                    color="#9a6b27"
                    dense
                    type="number"
                    :disabled="advancePresent"
                    hide-details
                    class="pro-input mr-2"
                  ></v-text-field>
                  <v-btn v-if="advancePresent" outlined color="#dc2626" class="text-capitalize settlement-action-btn" @click="removeAdvance()">
                    Remove
                  </v-btn>
                  <template v-else>
                    <v-btn dark color="#9a6b27" class="text-capitalize mr-1 elevation-0 settlement-action-btn" @click="applyAdvance()">Apply</v-btn>
                    <v-btn outlined color="#475569" class="text-capitalize settlement-action-btn" @click="applyMaxAdvance()">Max</v-btn>
                  </template>
                </div>
              </div>

              <div class="breakdown-row deduction-row" v-if="advancePresent && advanceApplied > 0">
                <span class="breakdown-label">Advance Applied:</span>
                <span class="breakdown-val">- ₹ {{ parseFloat(advanceApplied).toFixed(2) }}</span>
              </div>

              <!-- Discount Section -->
              <div class="discount-box pa-3 my-2 rounded-lg" v-if="payamount && !billPaidAmount1 && !isStandalone">
                <div class="d-flex align-center">
                  <v-text-field
                    outlined
                    placeholder="Discount amount (₹)"
                    v-model="discount"
                    color="#9a6b27"
                    dense
                    type="number"
                    :disabled="discountPresent"
                    hide-details
                    class="pro-input mr-2"
                  ></v-text-field>
                  <v-btn v-if="discountPresent == true" outlined color="#dc2626" class="text-capitalize settlement-action-btn" @click="removediscount()">
                    Remove
                  </v-btn>
                  <v-btn v-else dark color="#9a6b27" class="text-capitalize elevation-0 settlement-action-btn" @click="caldiscount()">
                    Apply
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Net Payable Box (Anchored to bottom of column) -->
          <div class="net-payable-card pa-3 mt-4 rounded-lg d-flex justify-space-between align-center" v-if="payamount">
            <span class="net-title">{{ isStandalone ? 'Amount Payable to Customer:' : 'Net Payable Amount:' }}</span>
            <span class="net-val">₹ {{ Number(payamount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
          </div>
        </v-col>

        <!-- Right Panel: Payment Modes & Split Tender -->
        <v-col cols="12" md="6" class="pl-md-6 mt-4 mt-md-0 d-flex flex-column justify-space-between">
          <div>
            <div class="d-flex align-center mb-3">
              <v-icon small color="#475569" class="mr-2">mdi-credit-card-outline</v-icon>
              <span class="card-section-title">Payment Allocation</span>
            </div>

              <!-- Add Payment Input Grid (3 clean columns, perfectly aligned) -->
              <div class="payment-input-strip pa-3 rounded-lg mb-3" v-if="paymentTypeList && paymentTypeList.length > 0 && Number(pendingAmount) > 0">
                <v-row dense align="start">
                  <v-col cols="12" sm="5">
                    <label class="field-micro-label">Payment Mode</label>
                    <v-select
                      :items="paymentTypeList"
                      v-model="paytype"
                      placeholder="Select Mode"
                      dense
                      hide-details
                      outlined
                      class="pro-input"
                      color="#9a6b27"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <label class="field-micro-label">Amount (₹)</label>
                    <v-text-field
                      v-model="purchaseAmount"
                      dense
                      placeholder="0.00"
                      outlined
                      hide-details
                      type="number"
                      class="pro-input"
                      color="#9a6b27"
                      @keydown.enter="addValues()"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="3">
                    <label class="field-micro-label d-none d-sm-block" style="visibility: hidden;">&nbsp;</label>
                    <v-btn dark color="#9a6b27" block class="text-capitalize elevation-0 add-payment-btn" @click="addValues()">
                      <v-icon left small>mdi-plus</v-icon>
                      Add
                    </v-btn>
                  </v-col>
                </v-row>
                <div v-if="isCashOnlySale" class="cash-only-hint d-flex align-center mt-2">
                  <v-icon x-small color="#9a6b27" class="mr-1">mdi-information-outline</v-icon>
                  <span>Silver / Diamond items are settled in cash only</span>
                </div>
              </div>

          <!-- Added Payments List -->
          <div class="recorded-payments-list mb-3">
            <div v-if="paymentType && paymentType.length > 0">
              <div class="text-muted text-caption mb-2 font-weight-bold uppercase">Recorded Tender Allocations:</div>
              <div
                v-for="(item, i) in paymentType"
                :key="i"
                class="payment-item-pill d-flex align-center justify-space-between pa-2 mb-2 rounded-lg"
              >
                <div class="d-flex align-center">
                  <v-icon small color="#64748b" class="mr-2">mdi-cash</v-icon>
                  <span class="payment-mode-tag">{{ item.paytype }}</span>
                </div>
                <div class="d-flex align-center">
                  <span class="payment-amount font-weight-bold mr-3">₹ {{ Number(item.purchaseAmount).toFixed(2) }}</span>
                  <v-btn icon x-small @click="paymentTypeList.push(item.paytype); calcPrice(item.purchaseAmount, i)">
                    <v-icon small color="#dc2626">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-else class="empty-tender py-4 text-center text-muted">
              Select payment method and allocate tender amount above.
            </div>
          </div>
        </div>

        <!-- Pending Amount Notice -->
          <div
            v-if="pendingAmount"
            :class="['pending-status-card pa-3 mt-4 rounded-lg d-flex justify-space-between align-center', Number(pendingAmount) > 0 ? 'status-has-pending' : 'status-fully-settled']"
          >
            <div class="d-flex align-center">
              <v-icon small :color="Number(pendingAmount) > 0 ? '#d97706' : '#059669'" class="mr-2">
                {{ Number(pendingAmount) > 0 ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
              </v-icon>
              <span class="pending-label">{{ Number(pendingAmount) > 0 ? 'Remaining Balance:' : 'Allocation Complete:' }}</span>
            </div>
            <span class="pending-val">₹ {{ Number(pendingAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
          </div>
        </v-col>
      </v-row>

      <!-- Bottom Action Bar -->
      <v-divider class="my-4"></v-divider>
      <div class="d-flex justify-space-between align-center flex-wrap settle-actions">
        <v-btn outlined color="#475569" class="text-capitalize px-4" @click="$router.push('/SalesList')">
          <v-icon left small>mdi-arrow-left</v-icon>
          Back to Register
        </v-btn>

        <v-btn
          dark
          color="#9a6b27"
          large
          class="generate-bill-btn text-capitalize px-8 elevation-1"
          @click="validate()"
        >
          <v-icon left>mdi-receipt-text-check-outline</v-icon>
          Generate Final Invoice
        </v-btn>
      </div>
    </v-card>

    <!-- Delete Item Dialog -->
    <v-dialog width="400px" v-model="dialoge">
      <v-card class="rounded-lg erp-modal-card pa-4">
        <div class="modal-title font-weight-bold text-slate-900 mb-2">Remove Item</div>
        <div class="text-muted text-body-2 mb-4">Are you sure you want to remove this item from the settlement?</div>
        <div class="d-flex justify-end gap-2">
          <v-btn text color="#64748b" class="text-capitalize mr-2" @click="dialoge = false">Cancel</v-btn>
          <v-btn dark color="#dc2626" class="text-capitalize elevation-0" @click="deleteItem()">Remove</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Old Gold Dialog -->
    <v-dialog width="400px" v-model="dialoge2">
      <v-card class="rounded-lg erp-modal-card pa-4">
        <div class="modal-title font-weight-bold text-slate-900 mb-2">Remove Old Gold Item</div>
        <div class="text-muted text-body-2 mb-4">Are you sure you want to remove this exchange item?</div>
        <div class="d-flex justify-end gap-2">
          <v-btn text color="#64748b" class="text-capitalize mr-2" @click="dialoge2 = false">Cancel</v-btn>
          <v-btn dark color="#dc2626" class="text-capitalize elevation-0" @click="deleteOldGold()">Remove</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      show: true,
      show1: true,
      discountPresent:false,
      timeout: 2000,
      msg: null,
      estno: null,
      salesmenCode: null,
      date: null,
      juwellaryId: localStorage.getItem("juwellaryId"),
      itemz: [],
      designList: [],
      SupplierList: [],
      List: [],
      saledata: [],
      List2: [],
      oldGoldData: [],
      List3: [],
      customerName: null,
      taxType: localStorage.getItem("GSTstatus"),
      address: null,
      city: null,
      contact: null,
      ItemList: [],
      dtype: false,
      payAmount: null,
      // payType: ["Cash", "Card"],
      discount: null,
      curId: null,
      dialoge: false,
      curId2: null,
      dialoge2: false,
      ItemTotal: 0,
      OldGoldTotal: 0,
      calTotal: 0,
      returnItemTotal: 0,
      returnItemData: 0,
      returnItemNetWt: 0,
      returnId: null,
      payamount: "",
      //payment
      paymentTypeList: ["Cash", "QR Code", "Swiping", "Bank Transfer", "Cheque"],
      arr1 : [],
      paytype: null,
      pendingAmount: "",
      purchaseAmount: "",
      paymentType: [],
      singleitem: {
        paytype: "",
        purchaseAmount: 0,
      },
      isJewelleryPayable:false,
      billPaidAmount1:"",
      paymentpending:false,
      paymentStatus:"",
      billDiscount:"",
      redeemableSchemes: [],
      selectedSchemeIds: [],
      schemeAllocations: [],
      newSchemeBalance: 0,
      schemeAppliedAmount: 0,
      actualBillTotal:0,
      // Advance
      customerAdvanceAvailable: 0,
      advanceInput: "",
      advanceApplied: 0,
      advancePresent: false,
    };
  },
  mounted() {
    this.getData();
    this.getOldGoldData();
    this.getReturnGoldData();
  },
  computed: {
    // A standalone Old Gold/Silver Purchase or Sales Return (started directly
    // from the Estimate page, with no items sold) has no purchased items -
    // in that case the shop owes the customer money, not the other way round.
    isStandalone() {
      return this.List && this.List.length === 0;
    },
    // Silver and Diamond items are settled in cash only - no exchange/cheque/etc.
    schemeAvailableTotal() {
      return this.redeemableSchemes.reduce((sum, x) => sum + (x.availableAmount || 0), 0);
    },
    isCashOnlySale() {
      return (
        this.List &&
        this.List.length > 0 &&
        this.List.every(
          (item) => item.metalType === "Silver" || item.metalType === "Diamond"
        )
      );
    },
  },
  watch: {
    ItemTotal() {
      this.calTotalAmount();
    },
    OldGoldTotal() {
      this.calTotalAmount();
    },
    returnItemTotal() {
      this.calTotalAmount();
    },
  },
  methods: {
    getPaymentTypeList() {
      return this.isCashOnlySale
        ? ["Cash"]
        : ["Cash", "QR Code", "Swiping", "Bank Transfer", "Cheque"];
    },
    toggleScheme(id) {
      if (this.selectedSchemeIds.includes(id)) {
        this.selectedSchemeIds = this.selectedSchemeIds.filter((x) => x !== id);
      } else {
        this.selectedSchemeIds = this.selectedSchemeIds.concat([id]);
      }
      this.recalculatePayable();
    },
    getRedeemableSchemes(customerId, preselected) {
      axios({
        url: "/goldscheme/customer/redeemable",
        method: "get",
        params: {
          customerId: customerId,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.redeemableSchemes = response.data.data || [];
            var ids = this.redeemableSchemes.map((x) => x._id);
            this.selectedSchemeIds = (preselected || []).filter((id) => ids.includes(id));
          } else {
            this.redeemableSchemes = [];
            this.selectedSchemeIds = [];
          }
          this.recalculatePayable();
        })
        .catch((err) => {
          console.log(err);
        });
    },
    removediscount() {
      this.discountPresent=false;
      this.discount=0;
      this.recalculatePayable();
    },
    caldiscount() {
      this.discountPresent=true;
      this.recalculatePayable();
    },
    applyAdvance() {
      var val = parseFloat(this.advanceInput);
      if (!val || val <= 0) {
        this.msg = "Please enter a valid advance amount";
        this.showsnackbar = true;
        return;
      }
      if (val > this.customerAdvanceAvailable) {
        this.msg = "Entered amount exceeds available advance (₹" + this.customerAdvanceAvailable.toFixed(2) + ")";
        this.showsnackbar = true;
        return;
      }
      var discountAmt = this.discountPresent ? (parseFloat(this.discount) || 0) : 0;
      var maxApplicable = Math.max(0, this.calTotal - discountAmt);
      if (val > maxApplicable) {
        this.msg = "Advance cannot exceed remaining payable amount (₹" + maxApplicable.toFixed(2) + ")";
        this.showsnackbar = true;
        return;
      }
      this.advanceApplied = val;
      this.advancePresent = true;
      this.recalculatePayable();
    },
    applyMaxAdvance() {
      var discountAmt = this.discountPresent ? (parseFloat(this.discount) || 0) : 0;
      var maxApplicable = Math.max(0, this.calTotal - discountAmt);
      var amountToApply = Math.min(this.customerAdvanceAvailable, maxApplicable);
      if (amountToApply <= 0) {
        this.msg = "No advance can be applied";
        this.showsnackbar = true;
        return;
      }
      this.advanceInput = amountToApply;
      this.advanceApplied = amountToApply;
      this.advancePresent = true;
      this.recalculatePayable();
    },
    removeAdvance() {
      this.advancePresent = false;
      this.advanceApplied = 0;
      this.advanceInput = "";
      this.recalculatePayable();
    },
    recalculatePayable() {
      var discountAmt = this.discountPresent ? (parseFloat(this.discount) || 0) : 0;
      var advanceAmt = this.advancePresent ? (parseFloat(this.advanceApplied) || 0) : 0;
      var net = (this.calTotal || 0) - discountAmt - advanceAmt;
      // Gold scheme balance is spent last, on what is left after discount and
      // advance, in the order the schemes were ticked - the same rule the
      // server uses when it redeems them, so both arrive at the same amount.
      this.schemeAllocations = [];
      this.schemeAppliedAmount = 0;
      if (!this.isStandalone && this.paymentStatus == 'Pending') {
        var schemeRemaining = Math.max(0, Math.round(net * 100) / 100);
        this.selectedSchemeIds.forEach((id) => {
          var scheme = this.redeemableSchemes.find((x) => x._id === id);
          if (!scheme || schemeRemaining <= 0) return;
          var take = Math.round(Math.min(scheme.availableAmount, schemeRemaining) * 100) / 100;
          if (take <= 0) return;
          this.schemeAllocations.push({ id: id, schemeNumber: scheme.schemeNumber || '', amount: take });
          schemeRemaining = Math.round((schemeRemaining - take) * 100) / 100;
        });
        this.schemeAppliedAmount = Math.round(this.schemeAllocations.reduce((sum, a) => sum + a.amount, 0) * 100) / 100;
        net = net - this.schemeAppliedAmount;
      }
      this.newSchemeBalance = Math.max(0, this.schemeAvailableTotal - this.schemeAppliedAmount);
      // A standalone Old Gold/Silver Purchase or Sales Return has no sale
      // items, so calTotal is always <= 0 (money the shop owes the
      // customer). Clamping that to 0 would hide the payable amount.
      net = this.isStandalone ? Math.abs(net) : Math.max(0, net);
      this.payamount = net.toFixed(2);
      this.pendingAmount = net.toFixed(2);
      this.paymentType = [];
      this.paytype = "";
      this.purchaseAmount = "";
      this.paymentTypeList = this.getPaymentTypeList();
    },
    calTotalAmount() {
      if(this.paymentStatus=='Pending'){
      console.log("zzzzz")
      if (this.OldGoldTotal || this.returnItemTotal) {
         this.calTotal = this.ItemTotal;
          console.log("initial-caltotal=",this.calTotal)
        if (this.OldGoldTotal) {
          var newgoldtotal= this.calTotal - this.OldGoldTotal;
          this.calTotal = newgoldtotal;
          console.log("afterOldgold-caltotal=",this.calTotal)
        }
        if (this.returnItemTotal) {
          var newreturntotal = this.calTotal - this.returnItemTotal;
          this.calTotal = newreturntotal;
          console.log("afterReturn-caltotal=",this.calTotal)
        }
      }
    
      else {
        this.calTotal = this.ItemTotal;
        console.log("amtwithout==", this.calTotal);
      }
      this.calTotalAmount2();
    }
    else{
      this.calTotalAmount3();
    }
    },
    calTotalAmount2() {
      if(this.billPaidAmount1>0 ){
          console.log("calTotal=",this.calTotal)
          console.log("billPaidAmount=",this.billPaidAmount1)
        if(this.billDiscount){
          this.calTotal = this.calTotal - this.billDiscount;
          console.log("caltotal after applied prev discount==", this.calTotal);
        }
        console.log("this.calTotal1111==", this.calTotal);
        console.log("this.billPaidAmount111111==", this.billPaidAmount1);
          this.calTotal = this.calTotal - this.billPaidAmount1;
          console.log("amtafterminuspaidamt==", this.calTotal);
        }
      this.recalculatePayable();
    },
    calTotalAmount3(){
      this.calTotal = this.newpayableAmount;
      this.recalculatePayable();
    },
    validate() {
      if (!this.paymentType) {
        this.msg = "Please choose payment type ";
        this.showsnackbar = true;
      } else if (!this.isStandalone && !this.saledata.totalAmount) {
        this.msg = "Please enter total amount ";
        this.showsnackbar = true;
      } else {
        this.finalPayment();
      }
    },
    // selectThis(item){
    //   console.log("item choosed=", item);
    //   var singleobj = this.goldSchemeData.find((x) => x._id == item);
    //   console.log("singleobj==", singleobj);
    //   if (singleobj.finalTotalAmount) {
    //     this.schemeAmtAvailable = singleobj.finalTotalAmount;
    //     console.log("schemeAmtAvailable=", this.schemeAmtAvailable);
    //   } 
    // },
    getData() {
      axios({
        url: "/sales/getlist",
        method: "get",
        params: {
          saleId: this.$route.query.no,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.List = response.data.data;
            this.paymentTypeList = this.getPaymentTypeList();
            if (this.taxType == "true") {
              console.log("A1")
              var ItemTotal1 = response.data.saledata.totalAmountWithGst;
              var ItemTotal2 = parseFloat(ItemTotal1);
              this.ItemTotal = ItemTotal2.toFixed(2);
              console.log("saletotal caltotal==",this.ItemTotal)


            } else {
              console.log("B1")

              var ItemTotal3 = response.data.saledata.totalAmountWithoutGst;
              var ItemTotal4 = parseFloat(ItemTotal3);
              this.ItemTotal = ItemTotal4.toFixed(2);
              console.log("saletotal caltotal==",this.ItemTotal)
            }
            // this.calTotalAmount();
            this.billPaidAmount1 = response.data.saledata.billPaidAmount;
            this.billDiscount = response.data.saledata.billDiscount;
            this.paymentStatus = response.data.saledata.paymentStatus;
            if(this.paymentStatus=='PartiallyPaid'){
              this.newpayableAmount =response.data.saledata.pendingAmount;
              this.actualBillTotal =response.data.saledata.initialBillPayableAmount;
            }
            console.log("billPaidAmount=",this.billPaidAmount1)
            this.saledata = response.data.saledata;
            this.customerAdvanceAvailable = (this.saledata && this.saledata.customerId && this.saledata.customerId.advanceAmount) ? parseFloat(this.saledata.customerId.advanceAmount) : 0;
            this.customerName = this.saledata.customerId.customerName;
            this.address = this.saledata.customerId.address;
            this.city = this.saledata.customerId.city;
            this.contact = this.saledata.customerId.mobileNumber;
            this.estno = this.saledata.billNo;
            var date1 = this.saledata.create_date;
            this.date = this.formatDate(date1);
            this.salesmenCode = this.saledata.staffCode;
            console.log("finished root")
            this.calTotalAmount();
            if (this.saledata.customerId && this.saledata.customerId._id) {
              this.getRedeemableSchemes(
                this.saledata.customerId._id,
                (this.saledata.goldSchemeSelections || []).map((x) => (x && x._id) || x)
              );
            }
          } else {
            // this.msg = response.data.msg;
            // this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    deleteItem() {
      axios({
        url: "/saleitem/delete",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.dialoge = false;
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.dialoge = false;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getOldGoldData() {
      axios({
        url: "/oldgolditem/getlist",
        method: "get",
        params: {
          salesId: this.$route.query.no,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.List2 = response.data.data;
            var OldGoldTotal1 = response.data.bill.totalAmount;
            this.OldGoldTotal = OldGoldTotal1.toFixed(2);
            this.oldGoldData = response.data.bill;
          } else {
            // this.msg = response.data.msg;
            // this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    deleteOldGold() {
      axios({
        url: "/oldgolditem/delete",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.curId2,
        },
      })
        .then((response) => {
          this.dialoge = false;
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.dialoge = false;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getReturnGoldData() {
      axios({
        url: "/returnitem/getlist",
        method: "get",
        params: {
          salesId: this.$route.query.no,
          jewelleryId: this.juwellaryId,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.List3 = response.data.data;
            this.returnId = response.data.bill._id;

            var returnItemTotal1 = response.data.bill.totalBillAmount;
            this.returnItemTotal = returnItemTotal1.toFixed(2);
            var returnItemNetWt1 = response.data.bill.totalNetWeight;
            this.returnItemNetWt = returnItemNetWt1.toFixed(2);
          } else {
            // this.msg = response.data.msg;
            // this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    downloadInvoices(){
      if(this.List.length>0){
        console.log("r1")
        this.downloadReciept();
      }
      if(this.List2.length>0){
        console.log("r2")
        this.downloadOldGoldReciept();
      }
      if(this.List3.length>0){
        console.log("r3")
        this.downloadReturnReciept();
      }
      console.log("r4")
      this.$router.push("/salesReport");
    },
    downloadReciept() {
      axios({
        method: "get",
        url: "/download/invoice",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.no,
        },
        responseType: "blob",
      })
        .then((response) => {
          // this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Receipt.pdf");
          document.body.appendChild(link);
          link.click();
          // this.$router.push("/salesReport");
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    downloadOldGoldReciept() {
      axios({
        method: "get",
        url: "/download/purchaseinvoice",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.no,
        },
        responseType: "blob",
      })
        .then((response) => {
          // this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "PurhaseInvoice.pdf");
          document.body.appendChild(link);
          link.click();
          // this.$router.push("/salesReport");
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    downloadReturnReciept() {
      axios({
        method: "get",
        url: "/download/voucher",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.no,
        },
        responseType: "blob",
      })
        .then((response) => {
          // this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Voucher.pdf");
          document.body.appendChild(link);
          link.click();
          // this.$router.push("/salesReport");
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    finalPayment() {
      //a bill fully covered by gold scheme balance is still a normal sale the
      //customer paid for - only a genuine shop-owes-customer case is payable
      if(this.isStandalone || (this.payamount<=0 && !(this.schemeAppliedAmount > 0))){
        this.isJewelleryPayable=true;
      }
      else{
        this.isJewelleryPayable=false;
      }
      axios({
        url: "/sales/payment",
        method: "POST",
        data: {
          jewelleryId: this.juwellaryId,
          isJewelleryPayable: this.isJewelleryPayable,
          saleId: this.$route.query.no,
          oldGoldId: this.oldGoldData._id,
          saleTotal: this.ItemTotal,
          // appliedOldGoldAmount: this.calTotal,
          oldGoldAmount: this.OldGoldTotal,
          paymentType: this.paymentType,
          billPendingAmount: this.pendingAmount,
          billDiscount: this.discount,
          advanceApplied: this.advancePresent ? (parseFloat(this.advanceApplied) || 0) : 0,
          payAmount: this.payamount,
          goldSchemeIds: this.schemeAllocations.map((a) => a.id),
          schemeAppliedAmount: this.schemeAppliedAmount,
          goldRate: localStorage.getItem("goldRate"),
          returnId: this.returnId,
          returnGoldAmount: this.returnItemTotal,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            localStorage.removeItem("billType");
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.ItemTotal="";
          this.calTotal = "";
         this.OldGoldTotal="";
          this.paymentType=[];
          this.pendingAmount="";
         this.discount="";
          this.advancePresent = false;
          this.advanceApplied = 0;
          this.advanceInput = "";
          this.customerAdvanceAvailable = 0;
          this.payamount="";
          this.returnId="";
          this.returnItemTotal="";
            this.downloadInvoices();
            // this.$router.push("/salesReport");
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    formatDate(item) {
      var dt = new Date(item);
      var day = dt.getDate();
      var year = dt.getFullYear();
      // var hours = dt.getHours();
      // var minutes = dt.getMinutes();
      dt = dt.toString();
      // var ampm = hours >= 12 ? "pm" : "am";
      // hours = hours % 12;
      // hours = hours ? hours : 12;
      // minutes = minutes < 10 ? "0" + minutes : minutes;
      var strTime = day + " " + dt.slice(4, 7) + " " + year;
      //  +
      // " , " +
      // hours +
      // ":" +
      // minutes +
      // " " +
      // ampm;

      return strTime;
    },
    //payment
    addValues() {
      console.log("purchaseAmount=", this.purchaseAmount);
      console.log("pendingAmount=", this.pendingAmount);
      if (this.paytype != "" && this.paytype != null && this.purchaseAmount != "") {
        console.log("purchaseAmount==",this.purchaseAmount);
        console.log("pendingAmount==",this.pendingAmount)

        if(parseFloat(this.purchaseAmount)<=parseFloat(this.pendingAmount)){
        this.singleitem.paytype = this.paytype;
        this.singleitem.purchaseAmount = this.purchaseAmount;
        console.log("type=", this.paytype);
        console.log("purchaseAmount=", this.purchaseAmount);

        console.log("1");
        
        this.arr1.push({ type: this.paytype, purchaseAmount: this.purchaseAmount });
        this.paymentTypeList = this.paymentTypeList.filter((x) => x !== this.paytype);
        console.log("paymentTypeList=",this.paymentTypeList)
        // this.paytype = "cash";
        // arr1.push({ type: this.paytype, purchaseAmount: this.purchaseAmount });
        // this.paymentTypeList = this.paymentTypeList.filter((x) => x != this.paytype);
        this.calcPrice2(this.purchaseAmount);
        }
        else if(parseFloat(this.purchaseAmount)>parseFloat(this.pendingAmount)){
          this.msg = "Amount is geater than pending amount";
        this.showsnackbar = true;
        }
        else{
          this.msg = "else case";
        this.showsnackbar = true;
        }
       

      } else {
        this.msg = "fields cannot be empty";
        this.showsnackbar = true;
      }
    },
    addVariant() {
      console.log("3");
      this.paymentType.push(JSON.parse(JSON.stringify(this.singleitem)));
      this.paytype = null;
      this.purchaseAmount = null;
      console.log("paymentType==", this.paymentType);
    },
    calcPrice2(val1) {
      console.log("2");
      var newPendingAmt = parseFloat(this.pendingAmount) - parseFloat(val1);
      if (Math.abs(newPendingAmt) < 0.5) newPendingAmt = 0;
      this.pendingAmount = newPendingAmt.toFixed(2);
      console.log("pendingAmount in calprice2=", this.pendingAmount);
      this.addVariant();
    },
    calcPrice(vall,i) {
      console.log("val=", vall);
      console.log("pendingAmount-before-remove=", this.pendingAmount);
      var newPendingAmt = parseFloat(this.pendingAmount) + parseFloat(vall);
      this.pendingAmount = newPendingAmt.toFixed(2);
      this.paymentType.splice(i, 1)
      console.log("paymentType==",this.paymentType)
      console.log("pendingamt after remove=", this.pendingAmount);
    },
    //
  },
};
</script>

<style scoped>
.settlement-page-wrapper {
  background-color: transparent;
  min-height: 100vh;
  padding: 12px 16px 80px 16px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

.settlement-header-card,
.customer-card,
.pro-content-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

.header-back-btn {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.header-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.header-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.estimate-pill {
  font-family: monospace;
  font-size: 12px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 2px 10px;
  border-radius: 9999px;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 4px 10px;
  border-radius: 6px;
}

.card-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: 0.2px;
}

.info-group {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 2px;
}

.info-val {
  font-size: 13px;
  color: #1e293b;
}

.card-bar {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.cursor-pointer {
  cursor: pointer;
}

.badge-count {
  font-size: 11px;
  font-weight: 600;
  background-color: #fef3c7;
  color: #92400e;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid #fde68a;
}

.table-responsive {
  overflow-x: auto;
}

.pro-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.text-muted {
  color: #94a3b8;
}

.text-emerald-700 {
  color: #047857 !important;
}

.text-slate-900 {
  color: #0f172a !important;
}

.border-right-md {
  border-right: 1px solid #e2e8f0;
}

@media (max-width: 959px) {
  .border-right-md {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 16px;
  }
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
}

.breakdown-label {
  color: #475569;
  font-weight: 500;
}

.breakdown-val {
  font-weight: 600;
  color: #0f172a;
}

.deduction-row .breakdown-label,
.deduction-row .breakdown-val {
  color: #047857;
}

.customer-advance-box,
.scheme-box,
.discount-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.advance-title {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.advance-avail-val {
  font-size: 13px;
  font-weight: 700;
  color: #047857;
}

.net-payable-card {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  min-height: 52px;
}

.net-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e3a8a;
}

.net-val {
  font-size: 18px;
  font-weight: 800;
  color: #1e3a8a;
}

.field-micro-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  display: block;
  margin-bottom: 4px;
  height: 15px;
  line-height: 15px;
}

.cash-only-hint {
  font-size: 11px;
  color: #9a6b27;
  font-weight: 500;
}

.pro-input ::v-deep .v-input__slot {
  min-height: 38px !important;
  max-height: 38px !important;
  background: #ffffff !important;
}

.pro-input ::v-deep fieldset {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused ::v-deep fieldset {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.settlement-action-btn {
  height: 38px !important;
  min-width: 76px;
  font-weight: 600 !important;
  font-size: 12px !important;
  border-radius: 6px !important;
}

.add-payment-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  height: 38px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  font-size: 12px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.payment-item-pill {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.payment-mode-tag {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
}

.payment-amount {
  font-size: 13px;
  color: #0f172a;
}

.pending-status-card {
  border-width: 1px;
  border-style: solid;
  min-height: 52px;
}

.status-has-pending {
  background: #fffbeb;
  border-color: #fde68a;
}

.status-has-pending .pending-label {
  color: #92400e;
  font-size: 13px;
  font-weight: 600;
}

.status-has-pending .pending-val {
  color: #92400e;
  font-size: 16px;
  font-weight: 800;
}

.status-fully-settled {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.status-fully-settled .pending-label {
  color: #065f46;
  font-size: 13px;
  font-weight: 600;
}

.status-fully-settled .pending-val {
  color: #065f46;
  font-size: 16px;
  font-weight: 800;
}

.generate-bill-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  border-radius: 10px !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.25s ease !important;
}

.generate-bill-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px) !important;
}

.erp-modal-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

/* ---- Phones ---- */
@media (max-width: 600px) {
  .net-payable-card,
  .pending-status-card {
    flex-wrap: wrap;
    gap: 4px 12px;
  }
  .net-val,
  .pending-val {
    margin-left: auto;
    white-space: nowrap;
  }
  .settle-actions {
    flex-direction: column-reverse;
    align-items: stretch !important;
    gap: 10px;
  }
  .settle-actions .v-btn {
    width: 100%;
    margin: 0;
  }
}
</style>
